/**
 * Batch get resolution states for multiple markets
 *
 * Uses viem's multicall to fetch all data in a single RPC call.
 */
import type { Address, MulticallParameters, MulticallReturnType } from "viem"
import { breakoutMarketAbi } from "../../abi/generated/breakoutMarket"
import type { Resolution } from "../../types"

/**
 * Client type that supports multicall operations.
 * Accepts any client with a multicall method (e.g., PublicClient).
 */
type MulticallClient = {
	multicall: <const contracts extends readonly unknown[], allowFailure extends boolean = true>(
		args: MulticallParameters<contracts, allowFailure>,
	) => Promise<MulticallReturnType<contracts, allowFailure>>
}

export interface GetResolutionsBatchParams {
	markets: Address[]
}

export interface GetResolutionsBatchResult {
	/** Map of market address to resolution state */
	resolutions: Map<string, Resolution>
	/** Markets that failed to fetch (with error message) */
	errors: Map<string, string>
}

/**
 * Get resolution states for multiple markets in a single RPC call
 *
 * Uses viem's multicall to batch all contract reads together,
 * significantly reducing latency compared to individual calls.
 *
 * NOTE: Results are stored with lowercase addresses as keys for consistent lookups.
 * You can pass either checksummed or lowercase addresses - lookups will work either way.
 *
 * @example
 * ```ts
 * import { getResolutionsBatch } from "@breakout/sdk/actions/read"
 *
 * const { resolutions, errors } = await getResolutionsBatch(publicClient, {
 *   markets: ["0x...", "0x...", "0x..."],
 * })
 *
 * for (const [market, resolution] of resolutions) {
 *   console.log(`${market}: ${resolution.isResolved ? "resolved" : "pending"}`)
 * }
 * ```
 */
export async function getResolutionsBatch(
	client: MulticallClient,
	params: GetResolutionsBatchParams,
): Promise<GetResolutionsBatchResult> {
	if (params.markets.length === 0) {
		return { resolutions: new Map(), errors: new Map() }
	}

	// Build multicall contracts array - 1 call per market (resolution)
	const contracts = params.markets.map(market => ({
		address: market,
		abi: breakoutMarketAbi,
		functionName: "resolution" as const,
	}))

	const results = await client.multicall({
		contracts,
		allowFailure: true,
	})

	const resolutions = new Map<string, Resolution>()
	const errors = new Map<string, string>()

	// Process results - normalize to lowercase for consistent lookups
	for (let i = 0; i < params.markets.length; i++) {
		const market = params.markets[i]
		if (!market) {
			continue
		}

		// Normalize to lowercase for consistent map lookups regardless of input casing
		const normalizedAddress = market.toLowerCase()
		const resolutionResult = results[i]

		if (resolutionResult?.status === "success") {
			const [winningOutcome, dataUri, nonce, isResolved, isFinalized] = resolutionResult.result as [
				number,
				string,
				number,
				boolean,
				boolean,
			]

			resolutions.set(normalizedAddress, {
				winningOutcome,
				isResolved,
				isFinalized,
				dataUri,
				nonce,
			})
		} else {
			const errorMsg =
				resolutionResult?.status === "failure" ? resolutionResult.error.message : "Unknown error"
			errors.set(normalizedAddress, errorMsg)
		}
	}

	return { resolutions, errors }
}
